import { Grid, Radio, RadioGroup } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import { DatePicker } from '@mui/lab';
import { FormControlLabel } from '@mui/material';
import { Checkbox } from '@mui/material';
import Italian from '../../shared/italian';
import Foreign from '../../shared/foreign';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';
import { FormControl } from '@mui/material';
import { ConfigurationType } from 'civil-status/constants/configuration-type';

interface PubbliDataProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
}

const PubbliDataComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  configuration,
  onChange,
  onChanges,
}: PubbliDataProps): React.ReactElement => {
  const isPubbli: boolean = useMemo(() => {
    return configuration === ConfigurationType.PUBBLI;
  }, [configuration]);

  const onChangeItalian = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onChangesItalian = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChange('foreign', {
        ...(values || {}).foreign,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onFieldsForeignChange = useCallback(
    (valuesNew) => {
      onChange('foreign', {
        ...(valuesNew || {}),
      });
    },
    [values, onChange]
  );

  return (
    <Grid container spacing={2} item>
      {isPubbli ? (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                id="isReductionTerms"
                checked={values?.isReductionTerms || false}
                onChange={() => {
                  onChange('isReductionTerms', !values?.isReductionTerms);
                }}
                inputProps={{ 'aria-label': 'Riduzione dei termini' }}
              />
            }
            label="Riduzione dei termini"
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data inizio *"
          aria-label="Data inizio"
          value={values?.dataStart || null}
          onChange={(date) => {
            onChange('dataStart', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dataStart"
              fullWidth
              helperText={errors?.dataStart}
              error={touched?.dataStart && Boolean(errors?.dataStart)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data fine *"
          aria-label="Data fine"
          value={values?.dataEnd || null}
          onChange={(date) => {
            onChange('dataEnd', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dataEnd"
              fullWidth
              helperText={errors?.dataEnd}
              error={touched?.dataEnd && Boolean(errors?.dataEnd)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Stato"
            id="isForeign"
            value={values?.isForeign || 'false'}
            onChange={() => {
              onChanges({
                ...(values || {}),
                isForeign: !values?.isForeign,
                italian: {
                  location: '',
                  province: '',
                  municipality: null,
                },
                foreign: {
                  state: null,
                  province: '',
                  municipality: null,
                },
              });
            }}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Italia"
              disabled={disabled}
            />
            <FormControlLabel value="true" control={<Radio />} label="Estero" disabled={disabled} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!values?.isForeign ? (
        <Italian
          disabledComponent={disabled}
          values={values?.italian}
          onChange={onChangeItalian}
          onChanges={onChangesItalian}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality="Comune ente"
          labelProvince="Provincia ente"
          required
          fromDate={values?.dataStart || null}
        />
      ) : (
        <Foreign
          disabledComponent={disabled}
          values={values?.foreign}
          onChange={onFieldForeignChange}
          onChanges={onFieldsForeignChange}
          labelState="Stato ente"
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
          isWithConsulate
        />
      )}
    </Grid>
  );
};

PubbliDataComponent.displayName = 'Form Pubbli Data component';

const PubbliData = memo(PubbliDataComponent);

export default PubbliData;
